/**
 * AdminProjectListPage
 * UC-023 - Admin Project Management
 */
import React, { useState, useEffect } from 'react';
import { Button, Input, Card } from '../components/ui';
import { Select } from '../components/ui/Select';
import { ChevronRight, Filter } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../core/api-client';
import toast from 'react-hot-toast';

interface Project {
  projectId: number;
  name: string;
  status: string;
  userId: number;
  userName?: string;
  userSurname?: string;
  creationDate: string;
}

export function AdminProjectListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [loading, setLoading] = useState(false);

  const statusOptions = [
    { value: '', label: t('admin.projects.list.allProjects', 'All projects') },
    { value: 'AppPreviewPreparation', label: 'App preview preparation' },
    { value: 'Rejected', label: 'Rejected' },
    { value: 'AppPreviewFeedback', label: 'App preview feedback' },
    { value: 'TechnicalAnalysis', label: 'Technical analysis' },
    { value: 'SprintPlanning', label: 'Sprint planning' },
    { value: 'InDevelopment', label: 'In development' },
    { value: 'ForTesting', label: 'For testing' },
    { value: 'FinishedTesting', label: 'Finished testing' },
    { value: 'Delivered', label: 'Delivered' }
  ];

  const fetchProjects = async () => {
    try {
      setLoading(true);
      // Omit the status param entirely when the filter is "All projects" so
      // the backend returns every project rather than rows with status == "".
      const params = selectedStatus ? { status: selectedStatus } : {};
      const response = await api.get<{items: Project[]}>('/projects', { params });
      const items = response.items || [];
      items.sort((a, b) => (b.creationDate || '').localeCompare(a.creationDate || ''));
      setProjects(items);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error(t('admin.projects.list.error'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [selectedStatus]);

  const filteredProjects = projects.filter(project => {
    if (!userFilter.trim()) return true;
    const searchTerm = userFilter.toLowerCase();
    const userName = `${project.userName || ''} ${project.userSurname || ''}`.toLowerCase();
    return userName.includes(searchTerm);
  });

  const selectProject = (project: Project) => {
    navigate(`/admin/projects/${project.projectId}`);
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'AppPreviewPreparation':
        return 'bg-amber-100 text-amber-800 border-amber-200';
      case 'Rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'AppPreviewFeedback':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'TechnicalAnalysis':
        return 'bg-cyan-100 text-cyan-800 border-cyan-200';
      case 'SprintPlanning':
        return 'bg-violet-100 text-violet-800 border-violet-200';
      case 'InDevelopment':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'ForTesting':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'FinishedTesting':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Delivered':
        return 'bg-emerald-100 text-emerald-800 border-emerald-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-full">
      <div className="mb-6">
        <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">
          {t('admin.projects.list.title')}
        </h1>
        <p className="mt-2 text-sm text-gray-600">
          {t('admin.projects.list.description')}
        </p>
      </div>

      {/* Filters */}
      <Card className="mb-6 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Status Filter */}
          <div>
            <Select<string>
              label={t('admin.projects.list.statusFilter')}
              value={selectedStatus}
              onChange={setSelectedStatus}
              options={statusOptions.map((o) => ({ value: o.value, label: o.label }))}
            />
          </div>

          {/* User Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('admin.projects.list.userFilter')}
            </label>
            <Input
              type="text"
              value={userFilter}
              onChange={(e) => setUserFilter(e.target.value)}
              placeholder={t('admin.projects.list.userFilterPlaceholder')}
              className="w-full"
            />
          </div>
        </div>
      </Card>

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8">
          <div className="text-gray-600">{t('common.loading')}</div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredProjects.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">{t('admin.projects.list.noProjects')}</div>
        </div>
      )}

      {/* Project List */}
      {!loading && filteredProjects.length > 0 && (
        <div className="space-y-4">
          {filteredProjects.map((project) => (
            <Card
              key={project.projectId}
              className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => selectProject(project)}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <h3 className="text-sm font-medium text-gray-900 truncate">
                    {project.name}
                  </h3>
                  <p className="text-sm text-gray-600 mt-1">
                    {project.userName} {project.userSurname}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {t('admin.projects.list.created')}: {new Date(project.creationDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    className={`px-2.5 py-1 text-xs rounded-full border ${getStatusBadgeClass(project.status)}`}
                  >
                    {statusOptions.find(s => s.value === project.status)?.label || project.status}
                  </span>
                  <ChevronRight className="h-5 w-5 text-gray-400" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default AdminProjectListPage;